import { isPlatformBrowser } from '@angular/common';
import { effect, inject, Injectable, PLATFORM_ID } from '@angular/core';
import posthog, { PostHogConfig } from 'posthog-js';
import { GlobalConfigurationService } from './global-configuration.service';
import { UserOrganizationStorageService } from './user-organization-storage.service';

@Injectable({
    providedIn: 'root'
})
export class PostHogService {
    private readonly globalConfigurationService: GlobalConfigurationService = inject(GlobalConfigurationService);
    private readonly userOrganizationStorageService: UserOrganizationStorageService =
        inject(UserOrganizationStorageService);
    private readonly platformId = inject(PLATFORM_ID);
    private initialized = false;
    private readonly host = 'https://us.i.posthog.com';

    constructor() {
        effect(() => {
            const org = this.userOrganizationStorageService.selectedOrganization();
            this.applyOrganization(org?.code);
        });
    }

    get posthog(): typeof posthog {
        if (isPlatformBrowser(this.platformId) && this.initialized) {
            return posthog;
        }

        return new Proxy({} as typeof posthog, {
            get: () => () => undefined
        });
    }

    init(): void {
        if (!isPlatformBrowser(this.platformId) || this.initialized) {
            return;
        }

        const apiKey = this.globalConfigurationService.getKey('PostHog__ProjectToken');

        if (!apiKey) {
            console.warn(
                'PostHog__ProjectToken is missing. PostHog will not be initialized and events will be skipped.'
            );
            return;
        }

        const options: Partial<PostHogConfig> = {
            api_host: this.host,
            disable_session_recording: true,
            capture_pageview: 'history_change',
            capture_exceptions: {
                capture_unhandled_errors: true,
                capture_unhandled_rejections: true,
                capture_console_errors: false
            },
            before_send: (event) => {
                if (!event) {
                    return event;
                }

                const pathname =
                    event.properties?.['$pathname'] || (typeof window !== 'undefined' ? window.location.pathname : '');
                const currentUrl = event.properties?.['$current_url'] || '';
                const isReports = pathname.startsWith('/reports') || currentUrl.includes('/reports');

                if (isReports && event.properties) {
                    delete event.properties['organizationId'];
                }

                return event;
            }
        };

        try {
            posthog.init(apiKey, options);
        } catch (error) {
            console.warn('PostHog init failed. PostHog was not initialized and events will be skipped.', error);
            return;
        }

        this.initialized = true;
    }

    private applyOrganization(organizationCode?: string): void {
        if (!this.initialized) {
            return;
        }

        if (organizationCode) {
            posthog.register({ organizationId: Number(organizationCode) });
            return;
        }

        posthog.unregister('organizationId');
    }
}
